import { ApplicationRef, ComponentRef, createComponent, EnvironmentInjector, Injectable, inject } from '@angular/core';
import html2pdf from 'html2pdf.js';
import { firstValueFrom } from 'rxjs';
import { Space } from '../models/space.model';
import { PdfPreviewComponent } from '../components/pdf-preview/pdf-preview.component';

@Injectable({
  providedIn: 'root'
})
export class PdfExportService {
  private readonly appRef = inject(ApplicationRef);
  private readonly environmentInjector = inject(EnvironmentInjector);

  async exportSpaceAsHtml(space: Space, filename?: string): Promise<void> {
    const hostDiv = document.createElement('div');
    hostDiv.style.cssText = 'position:fixed;left:-9999px;top:0;z-index:-1;width:794px;background:#fff';
    document.body.appendChild(hostDiv);

    let componentRef: ComponentRef<PdfPreviewComponent> | null = null;

    try {
      componentRef = createComponent(PdfPreviewComponent, {
        environmentInjector: this.environmentInjector,
        hostElement: hostDiv
      });
      componentRef.setInput('space', space);
      this.appRef.attachView(componentRef.hostView);
      componentRef.changeDetectorRef.detectChanges();

      // Wait for all async data (forkJoin) to complete
      await firstValueFrom(componentRef.instance.ready$);

      // Allow one animation frame for Angular to flush DOM updates
      await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));
      componentRef.changeDetectorRef.detectChanges();

      const element = hostDiv.firstElementChild as HTMLElement;
      const safeName = (filename ?? space.name ?? 'amplify-space').replace(/[^a-z0-9_\-\s]/gi, '_');

      // html2pdf.js has no official TypeScript types; cast to any for the options call
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const h2p = html2pdf as any;
      await h2p()
        .set({
          margin: [12.7, 0, 0, 0],
          filename: `${safeName}.pdf`,
          image: { type: 'jpeg', quality: 0.92 },
          html2canvas: { scale: 2, useCORS: true, allowTaint: false, backgroundColor: '#ffffff', logging: false, windowWidth: 794 },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
          pagebreak: { mode: ['css', 'avoid-all'] }
        })
        .from(element)
        .save();
    } finally {
      if (componentRef) {
        this.appRef.detachView(componentRef.hostView);
        componentRef.destroy();
      }
      document.body.removeChild(hostDiv);
    }
  }

  // ─── Legacy methods removed ───────────────────────────────────────────────
  // exportMainContent, extractContentFromDOM, extractSectionData,
  // fetchImageAsBase64, resizeBase64Image, captureElementAsImage,
  // buildDocumentDefinition — all deleted; use exportSpaceAsHtml() instead.

  /** @deprecated Use exportSpaceAsHtml() instead. This stub will be removed. */
  async exportMainContent(_filename: string = 'amplify-content'): Promise<void> {
    throw new Error('exportMainContent has been removed. Use exportSpaceAsHtml() instead.');
  }
}

